import {
  CellValueChangedEvent,
  ColDef,
  GridReadyEvent,
  ICellRendererParams,
} from '@ag-grid-community/core'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@sureco/design-system'
import {
  Layout as AdLayout,
  Input,
  Switch,
} from '@surecompanies/core_components'
import { message } from 'antd'
import axios from 'axios'
import { getMyClients } from 'enrollment-platform/api/persons'
import { SimpleCustomerDto } from 'enrollment-platform/api/persons/dto/simple-customer.dto'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PageWrapper from '../../../../PageWrapper'
import {
  CensusReviewCsvDownloadType,
  updateCensusFileRows,
} from '../../api/census-enrollment-platform-api-client'
import { CreateCensusFieldIgnoreRuleRequestDto } from '../../api/dto/census-field-ignore-rule.dto'
import { CensusFileMemberUpdatesSchema } from '../../api/dto/census-file-member-updates-schema.dto'
import { CensusFileRowCountsDto } from '../../api/dto/census-file-row-counts.dto'
import { CensusFileRowDto } from '../../api/dto/census-file-row.dto'
import { CensusFileRowsSelectAllDto } from '../../api/dto/census-file-rows-select-all.dto'
import { CensusFileDto } from '../../api/dto/census-file.dto'
import { CensusFileRowStatusEnum } from '../../api/enums/census-file-row-status.enum'
import { CensusFileRowTypeEnum } from '../../api/enums/census-file-row-type.enum'
import { PatchActionEnum } from '../../api/enums/patch-action.enum'
import FileInfo from '../../components/FileInfo'
import InlineEditActionButtons from '../../components/InlineEditActionButtons'
import ToggleEditModeButton from '../../components/ToggleEditModeButton'
import { CENSUS_REVIEW_PATH } from '../../constants'
import { useMarkRowDirtyUpdates } from '../../hooks/use-mark-row-dirty-updates'
import style from '../../pages/CensusManagement.module.scss'
import { CensusFileRowGridData } from '../../types/census-file-row-grid-data'
import { EligibilityData } from '../../types/eligibility-data'
import { formatIfDate } from '../../utils/format-if-date'
import { getUpdateGridContextMenuItems } from '../../utils/get-update-grid-context-menu-items'
import { mapGridDataToCensusFileRowDto } from '../../utils/map-grid-data-to-census-file-row-dto'
import { SAVE_CHANGES_BEFORE_APPROVAL_MESSAGE } from '../constants/census-review.constant'
import {
  getEligibilityBaselineUpdatesFromRow,
  hasAnyUnsavedEligibilityChanges,
} from '../utils/eligibility-changes.util'
import { getInReviewPageData } from '../utils/get-in-review-page-data'
import { CENSUS_TICKET_JOBS_QUERY_KEY } from '../hooks/useCensusTicketJobsPolling'
import ApproveSelectedUpdatesButton from './ApproveSelectedUpdatesButton'
import BackgroundTicketJobs from './BackgroundTicketJobs'
import CensusReviewCsvExportButton from './CensusReviewCsvExportButton'
import ReviewTabs from './CensusReviewTabs'
import RejectSelectedButton from './RejectSelectedButton'
import TrackSelectedUpdatesButton from './TrackSelectedUpdatesButton'
import UpdateRecordRow from './UpdateRecordRow'

const { Content } = AdLayout

interface Correction {
  field: string
  newValue: any
}

interface RowWithCorrections {
  allRowData: any
  corrections: Correction[]
}

// Todo: try to incorporate RowData as a data structure for the output
interface _RowData {
  field: string
  liveValue: any
  newValue: any
}

interface CensusReviewRecordsBaseProps {
  routeParams: { importPublicKey: string }
  actionButton?: ({
    censusRows,
    handleSubmit,
    selectedRows,
  }: {
    censusRows: any[]
    selectedRows: any[]
    handleSubmit: () => void
  }) => React.ReactElement
  rowQueryParams: {
    censusRowType: CensusFileRowTypeEnum | CensusFileRowTypeEnum[]
    censusFileRowStatuses: CensusFileRowStatusEnum[]
  }
  handleCellValueChanged?: (
    cellValueChangedEvent: CellValueChangedEvent
  ) => void
  modifyColDefs?: (
    baseColDefs: ColDef<CensusFileRowGridData>[]
  ) => ColDef<CensusFileRowGridData>[]
  setLoading: (loading: boolean) => void
  showSearch?: boolean
}

function CensusReviewRecordsBaseUpdate({
  routeParams,
  actionButton,
  rowQueryParams,
  setLoading,
  showSearch = false,
}: CensusReviewRecordsBaseProps) {
  const { censusRowType: paramCensusRowType, censusFileRowStatuses } =
    rowQueryParams

  // Declare all hooks at the top level
  const [counts, setCounts] = useState<CensusFileRowCountsDto | undefined>()
  const [censusFile, setCensusFile] = useState<CensusFileDto | undefined>()
  const { importPublicKey: publicKey } = routeParams
  const queryClient = useQueryClient()
  const [censusRowType, setCensusRowType] = useState<
    CensusFileRowTypeEnum | CensusFileRowTypeEnum[]
  >(paramCensusRowType)
  const [selectedRows, setSelectedRows] = useState<CensusFileRowGridData[]>([])
  const [censusRows, setCensusRows] = useState<CensusFileRowGridData[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [clients, setClients] = useState<SimpleCustomerDto[]>([])
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({})
  const [collapsedRows, setCollapsedRows] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(0)

  const toggleRow = (employeeId: string) => {
    setCollapsedRows((prev) => {
      const next = new Set(prev)
      next.has(employeeId) ? next.delete(employeeId) : next.add(employeeId)
      return next
    })
  }
  const [mode, setMode] = useState<'edit' | 'submit'>('submit')
  // Kill-switch for the ignore-fields capability. When the flag is off we
  // hide every UI affordance that would let a reviewer mutate ignore-rule
  // state — i.e. the per-field Ignore Switch in Edit Mode. Read-only
  // visibility of already-ignored fields (yellow highlight, the "Show
  // ignored values" toggle, the bulk approve button) is intentionally
  // left alone so any rules already in the DB still surface cleanly.
  const [searchTerm, setSearchTerm] = useState('')
  const prevSearchTermRef = useRef(searchTerm)

  // const [prevRows, setPrevRows] = useState<CensusFileRowGridData[]>([]);
  const gridRef = useRef<GridReadyEvent | undefined>(undefined)
  const clientName = (key) => clients.find((c) => c.id === key)?.name ?? '...'
  const prevRowsRef = useRef<CensusFileRowGridData[]>([])
  const [refreshKey, setRefreshKey] = useState(0)

  const [eligibilityData, setEligibilityData] = useState<
    Record<string, EligibilityData>
  >({})
  const [savedEligibilityByEmployeeId, setSavedEligibilityByEmployeeId] =
    useState<Record<string, EligibilityData>>({})

  const hasPendingChanges = useMemo(
    () =>
      hasAnyUnsavedEligibilityChanges(
        eligibilityData,
        savedEligibilityByEmployeeId
      ),
    [eligibilityData, savedEligibilityByEmployeeId]
  )

  // Reset logic (clearing selected rows and selection state)
  const resetGrid = useCallback(() => {
    gridRef.current?.api.refreshServerSide({ purge: true })
    gridRef.current?.api.deselectAll()
    setSelectedRows([])
    setIsSelectAll(false)
    setCheckedItems({})
    setCollapsedRows(new Set())
    setEligibilityData({})
    setSavedEligibilityByEmployeeId({})
    setCurrentPage(0)
    setRefreshKey((k) => k + 1)
    // Approving updates kicks off a background Zendesk ticket job; refetch so the
    // "Zendesk Ticket Jobs" section surfaces it immediately.
    queryClient.invalidateQueries({
      queryKey: [CENSUS_TICKET_JOBS_QUERY_KEY, publicKey],
    })
  }, [queryClient, publicKey])

  const pageSize = 100 // Number of employees per page

  // Tab change logic
  const onTabChange = useCallback((tab: string) => {
    setCensusRowType(tab as CensusFileRowTypeEnum)
    setSelectedRows([])
    setIsSelectAll(false)
    setCheckedItems({})
    setCollapsedRows(new Set())
    setSearchTerm('')
    setCurrentPage(0)
    setEligibilityData({})
    setSavedEligibilityByEmployeeId({})
  }, [])
  const fetchClients = async () => {
    const data = await getMyClients()
    setClients(data.sort((a, b) => a.name.localeCompare(b.name)))
  }
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchClients()
    }, 1)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const searchChanged = prevSearchTermRef.current !== searchTerm
    prevSearchTermRef.current = searchTerm

    if (searchChanged && currentPage !== 0) {
      setCurrentPage(0)
      return
    }

    setLoading(true)
    // Fetch data when component is mounted or censusRowType changes
    const fetchData = async () => {
      try {
        const { rowCounts, data, pagination, censusFile } =
          await getInReviewPageData({
            limit: pageSize,
            page: currentPage + 1,
            publicKey,
            censusRowType,
            censusFileRowStatuses,
            search: searchTerm || undefined,
          })

        setCounts(rowCounts)
        setCensusFile(censusFile)
        setTotalRows(pagination.total ?? 0)
        const mergedData: CensusFileRowGridData[] = data.map((d) => ({
          ...d,
          ...d.rowData,
        }))
        setCensusRows(mergedData)
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error fetching data:', error)
        }
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [censusRowType, publicKey, searchTerm, currentPage, refreshKey])

  const totalPages = Math.ceil(totalRows / pageSize)

  const currentEmployees = censusRows

  // Handle next and previous buttons
  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1)
    }
  }

  const getActiveBenefits = (update: any): string => {
    if (!update) return ''

    if (Array.isArray(update.activeBenefits)) {
      // Use reduce to remove duplicates
      const uniqueBenefits = update.activeBenefits?.reduce(
        (acc: string[], benefit: string) => {
          if (!acc.includes(benefit)) {
            acc.push(benefit)
          }
          return acc
        },
        []
      )
      return uniqueBenefits.join(', ')
    }
    return '' // Return an empty string if there are no active benefits
  }

  /** Session UI: rowId:fieldName -> 'pending' (saved on Save Edits) */
  const [locallyIgnoredFields, setLocallyIgnoredFields] = useState<
    Record<string, 'pending'>
  >({})
  /**
   * Batched to POST updateCensusFileRows on Save Edits.
   * `censusRowId` is tracked for undo lookup and ignore-only row merge.
   */
  const [pendingIgnoreCreates, setPendingIgnoreCreates] = useState<
    Array<
      CreateCensusFieldIgnoreRuleRequestDto & {
        censusRowId: number
        gridRow: CensusFileRowGridData
        displayEmployeeId: string
        displayName: string
      }
    >
  >([])

  const makeIgnoreKey = (rowId: number, field: string) => `${rowId}:${field}`

  const { markRowDirty, dirtyRows, cleanAllRows, undoLastEdit } =
    useMarkRowDirtyUpdates(prevRowsRef.current, setCensusRows)

  type UndoEntry = { type: 'cell' } | { type: 'ignore'; key: string }
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([])

  const hasUnsavedEditSession = undoStack.length > 0
  const hasBlockingPendingWork = hasPendingChanges || hasUnsavedEditSession

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasBlockingPendingWork) {
        event.preventDefault()
        event.returnValue =
          'You have unsaved changes! Are you sure you want to leave?'
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasBlockingPendingWork])

  const populateUpdateRowData = useCallback(
    (row: any): any[] => {
      if (row == null) return []
      const up: any[] = []

      if (row.updates?.updateFields) {
        Object.keys(row.updates.updateFields).forEach((key) => {
          const update = row.updates.updateFields[key]
          const ignoreKey = makeIgnoreKey(row.id, key)
          const ignoreState = locallyIgnoredFields[ignoreKey]
          up.push({
            field: key,
            liveValue: formatIfDate(formatForGrid(update.liveValue ?? '')),
            newValue: formatForGrid(update.newValue ?? ''),
            employeeName: `${row.firstName} ${row.lastName}`,
            employeeId: row.employeeId,
            rowId: row.id,
            proposedInitialValue: update.newValue ?? '',
            allRowData: row,
            isIgnored: ignoreState === 'pending',
            ignoreRuleId: undefined as number | undefined,
          })
        })
      }
      return up
    },
    [locallyIgnoredFields]
  )

  const formatForGrid = (value: any): string | number => {
    if (typeof value === 'boolean') {
      return value ? 'True' : 'False'
    }
    if (value === null || value === undefined) {
      return ''
    }
    return value
  }

  const handleIgnoreField = useCallback(
    (gridRow: {
      field: string
      rowId: number
      allRowData: CensusFileRowGridData
    }): boolean => {
      const { field, rowId, allRowData } = gridRow
      const employeePublicKey = allRowData.updates?.employeePublicKey
      const update = allRowData.updates?.updateFields?.[field]
      if (!employeePublicKey || !update) {
        message.error('Missing data to ignore this field.')
        return false
      }
      const key = makeIgnoreKey(rowId, field)
      const payload = {
        employeePublicKey,
        fieldName: field,
        ignoredNewValue: update.newValue as string | number | boolean,
        liveValue: String(update.liveValue ?? ''),
        censusRowId: rowId,
        gridRow: allRowData,
        displayEmployeeId: String(allRowData.employeeId ?? '-'),
        displayName:
          `${allRowData.firstName ?? ''} ${allRowData.lastName ?? ''}`.trim() ||
          '-',
      }
      setLocallyIgnoredFields((prev) => ({ ...prev, [key]: 'pending' }))
      setPendingIgnoreCreates((prev) => {
        const filtered = prev.filter((p) => {
          const sameEmployeeField =
            p.employeePublicKey === employeePublicKey && p.fieldName === field
          return !sameEmployeeField
        })
        return [...filtered, payload]
      })
      message.success(
        `Field "${field}" will be saved as ignored when you save edits.`
      )
      return true
    },
    []
  )

  const handleUnignorePending = useCallback(
    (rowId: number, fieldName: string, allRowData: CensusFileRowGridData) => {
      const key = makeIgnoreKey(rowId, fieldName)
      const employeePublicKey = allRowData.updates?.employeePublicKey
      setLocallyIgnoredFields((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
      if (employeePublicKey) {
        setPendingIgnoreCreates((prev) =>
          prev.filter(
            (p) =>
              !(
                p.employeePublicKey === employeePublicKey &&
                p.fieldName === fieldName
              )
          )
        )
      }
    },
    []
  )

  const cleanAllRowsAndStack = useCallback(() => {
    cleanAllRows()
    setUndoStack([])
    setLocallyIgnoredFields({})
    setPendingIgnoreCreates([])
  }, [cleanAllRows])

  const handleUndoLast = useCallback(() => {
    if (undoStack.length === 0) return
    const last = undoStack[undoStack.length - 1]
    if (last.type === 'ignore') {
      const entry = pendingIgnoreCreates.find(
        (p) => makeIgnoreKey(p.censusRowId, p.fieldName) === last.key
      )
      if (!entry) return
      handleUnignorePending(entry.censusRowId, entry.fieldName, entry.gridRow)
    } else {
      undoLastEdit()
    }
    setUndoStack((prev) => prev.slice(0, -1))
  }, [undoStack, pendingIgnoreCreates, handleUnignorePending, undoLastEdit])

  const handleToggleIgnore = useCallback(
    (
      data: {
        field: string
        rowId: number
        allRowData: CensusFileRowGridData
        isIgnored: boolean
      },
      shouldIgnore: boolean
    ) => {
      if (shouldIgnore) {
        const ignored = handleIgnoreField(data)
        if (ignored) {
          setUndoStack((prev) => [
            ...prev,
            { type: 'ignore', key: makeIgnoreKey(data.rowId, data.field) },
          ])
        }
      } else {
        handleUnignorePending(data.rowId, data.field, data.allRowData)
        const key = makeIgnoreKey(data.rowId, data.field)
        setUndoStack((prev) => {
          const idx = prev.findIndex(
            (e) => e.type === 'ignore' && e.key === key
          )
          return idx === -1
            ? prev
            : [...prev.slice(0, idx), ...prev.slice(idx + 1)]
        })
      }
    },
    [handleIgnoreField, handleUnignorePending]
  )

  const finalColDefs = useMemo((): ColDef[] => {
    const base: ColDef[] = [
      {
        headerName: 'Field',
        field: 'field',
        flex: 1,
      },
      {
        headerName: 'Current',
        field: 'liveValue',
        flex: 1,
      },
      {
        headerName: 'Proposed',
        field: 'newValue',
        flex: 1,
        editable: (params) => mode === 'edit' && !params.data?.isIgnored,
      },
    ]
    if (mode === 'edit') {
      base.push({
        headerName: 'Ignore',
        field: 'isIgnored',
        flex: 0.6,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams) => {
          if (!params.data) return null
          return (
            <Switch
              size="small"
              checked={params.data.isIgnored}
              onChange={(checked) => handleToggleIgnore(params.data, checked)}
            />
          )
        },
      })
    }
    return base
  }, [mode, handleToggleIgnore])

  // Handle checkbox change for each row
  const handleCheckboxChange = (e, row) => {
    if (hasBlockingPendingWork) {
      e.preventDefault() // Stop checkbox from toggling
      alert(SAVE_CHANGES_BEFORE_APPROVAL_MESSAGE)
    } else {
      const isChecked = checkedItems[row.employeeId]
      // If unchecking a row, turn off selectAll since it's no longer "all"
      if (isChecked) {
        setIsSelectAll(false)
        setSelectedRows((prev) =>
          prev.filter((r) => r.employeeId !== row.employeeId)
        )
      } else {
        setSelectedRows((prev) => [
          ...prev.filter((r) => r.employeeId !== row.employeeId),
          row,
        ])
      }
      setCheckedItems((prevCheckedItems) => {
        const newCheckedItems = { ...prevCheckedItems }
        if (newCheckedItems[row.employeeId]) {
          delete newCheckedItems[row.employeeId]
        } else {
          newCheckedItems[row.employeeId] = true
        }
        return newCheckedItems
      })
    }
  }

  // Submit and Reject handlers
  const handleSubmit = () => {
    resetGrid()
    // the resetGrid will set the current page to 0,
    // which will trigger the useEffect to fetch data
  }

  useEffect(() => {
    prevRowsRef.current = censusRows
  }, [censusRows])

  // Function to generate the clickable employee link
  const generateCoverageSummaryLink = (
    employeePublicKey: string,
    firstName: string,
    lastName: string
  ) => {
    return (
      <a
        href={`/employees/${employeePublicKey}/coverage`}
        target="_blank"
        style={{
          textDecoration: 'underline',
          fontWeight: 'bold',
          color: '#003146',
        }}
        title="Link to the coverage summary"
        onClick={(e) => {
          if (hasPendingChanges) {
            e.preventDefault() // Stop navigation
            alert('Please save your changes before navigating!')
          }
        }}
      >
        {firstName} {lastName}
      </a>
    )
  }

  const handleToggleChange = (employeeId: string, checked: boolean) => {
    setEligibilityData((prev) => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        benefitsEligible: checked,
      },
    }))
  }

  const handleDoNotCreateEnrollmentChange = (
    employeeId: string,
    checked: boolean
  ) => {
    setEligibilityData((prev) => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        doNotCreateEnrollment: checked,
      },
    }))
  }

  const handleDateChange = (
    employeeId: string,
    field: string,
    date: Date | null
  ) => {
    console.log('employeeId', employeeId, 'field', field, 'date:', date)

    setEligibilityData((prev) => {
      console.log('prev', prev[employeeId])
      const existingData = prev[employeeId] || {
        benefitsEligible: false, // Default values to retain structure
        benefitsEffectiveDate: null,
        enrollmentStartDate: null,
        enrollmentEndDate: null,
        doNotCreateEnrollment: false,
      }

      const updatedEligibilityData = {
        ...prev,
        [employeeId]: {
          ...existingData, // ✅ Keep all existing values
          [field]: date, // ✅ Only update the changed field
        },
      }
      return updatedEligibilityData
    })
  }

  useEffect(() => {
    setEligibilityData((prev) => {
      const newEligibilityData = { ...prev }
      let shouldUpdate = false // ✅ Prevent unnecessary state updates

      currentEmployees.forEach((row) => {
        if (!newEligibilityData[row.employeeId]) {
          newEligibilityData[row.employeeId] =
            getEligibilityBaselineUpdatesFromRow(row)
          shouldUpdate = true
        }
      })

      return shouldUpdate ? newEligibilityData : prev
    })

    setSavedEligibilityByEmployeeId((prev) => {
      const newSavedEligibility = { ...prev }
      let shouldUpdate = false

      currentEmployees.forEach((row) => {
        if (!newSavedEligibility[row.employeeId]) {
          newSavedEligibility[row.employeeId] =
            getEligibilityBaselineUpdatesFromRow(row)
          shouldUpdate = true
        }
      })

      return shouldUpdate ? newSavedEligibility : prev
    })
  }, [currentEmployees])

  const updateMemberUpdates = (
    employeeId: string,
    updates: Partial<CensusFileMemberUpdatesSchema>
  ) => {
    const savedEligibility: EligibilityData = {
      benefitsEligible: updates.benefitsEligible ?? false,
      benefitsEffectiveDate: updates.benefitsEffectiveDate ?? null,
      enrollmentStartDate: updates.enrollmentStartDate ?? null,
      enrollmentEndDate: updates.enrollmentEndDate ?? null,
      doNotCreateEnrollment: updates.doNotCreateEnrollment ?? false,
    }

    setCensusRows((prevRows) =>
      prevRows.map((row) =>
        row.employeeId === employeeId
          ? {
              ...row,
              memberUpdates: {
                ...row.memberUpdates, // Preserve existing values
                ...updates, // Merge new updates
              },
            }
          : row
      )
    )
    // Keep the selection snapshot in sync so Approve/Track use saved memberUpdates
    // for checked rows, not the data from when the row was first selected.
    setSelectedRows((prev) =>
      prev.map((row) =>
        row.employeeId === employeeId
          ? {
              ...row,
              memberUpdates: {
                ...row.memberUpdates,
                ...updates,
              },
            }
          : row
      )
    )
    setSavedEligibilityByEmployeeId((prev) => ({
      ...prev,
      [employeeId]: savedEligibility,
    }))
  }

  // Track whether "Select All" (all items in the backend) is active
  const [isSelectAll, setIsSelectAll] = useState(false)

  // Build the selectAll payload to send to the API instead of individual rows
  const selectAllPayload: CensusFileRowsSelectAllDto | undefined =
    useMemo(() => {
      if (!isSelectAll) return undefined
      const types = Array.isArray(censusRowType)
        ? censusRowType
        : [censusRowType]
      return {
        selectAll: true as const,
        type: types,
        status: censusFileRowStatuses,
        search: searchTerm || undefined,
      }
    }, [isSelectAll, censusRowType, censusFileRowStatuses, searchTerm])

  const isAllFullySelected =
    isSelectAll ||
    (totalRows > 0 && Object.keys(checkedItems).length >= totalRows)

  const handleSelectAll = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    if (!checked) {
      setIsSelectAll(false)
      setCheckedItems({})
      setSelectedRows([])
      return
    }

    if (hasBlockingPendingWork) {
      e.preventDefault()
      alert(SAVE_CHANGES_BEFORE_APPROVAL_MESSAGE)
      return
    }

    if (!totalRows) return

    try {
      setLoading(true)
      const { data } = await getInReviewPageData({
        limit: totalRows,
        page: 1,
        publicKey,
        censusRowType,
        censusFileRowStatuses,
        search: searchTerm || undefined,
      })
      const allRows: CensusFileRowGridData[] = data.map((d) => ({
        ...d,
        ...d.rowData,
      }))
      const newCheckedItems: Record<string, boolean> = {}
      allRows.forEach((row) => {
        newCheckedItems[row.employeeId] = true
      })
      setIsSelectAll(true)
      setCheckedItems(newCheckedItems)
      setSelectedRows(allRows)
    } catch (error) {
      if (axios.isCancel(error)) return
      setIsSelectAll(false)
      console.error('Error fetching all rows for select all:', error)
    } finally {
      setLoading(false)
    }
  }

  const isCurrentPageFullySelected =
    currentEmployees.length > 0 &&
    currentEmployees.every((row) => checkedItems[row.employeeId])

  const handleSelectAllInCurrentPageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (hasBlockingPendingWork) {
      e.preventDefault() // Stop checkbox from toggling
      alert(SAVE_CHANGES_BEFORE_APPROVAL_MESSAGE)
    } else {
      const checked = e.target.checked

      // If unchecking rows on this page, reset global selectAll so
      // selectAllPayload is not sent and isAllFullySelected recalculates
      if (!checked) {
        setIsSelectAll(false)
      }

      const newCheckedItems = { ...checkedItems }
      currentEmployees.forEach((row) => {
        if (checked) {
          newCheckedItems[row.employeeId] = true
        } else {
          delete newCheckedItems[row.employeeId]
        }
      })
      setCheckedItems(newCheckedItems)

      setSelectedRows((prev) => {
        const filtered = prev.filter(
          (r) => !currentEmployees.some((cr) => cr.employeeId === r.employeeId)
        )
        return checked ? [...filtered, ...currentEmployees] : filtered
      })
    }
  }

  useEffect(() => {
    prevRowsRef.current = censusRows
  }, [censusRows])

  // selectedRows is a snapshot from checkbox selection and can go stale after
  // saves or other grid updates. Merge each selected row with the latest
  // censusRows entry before Approve/Track so those actions submit current data.
  // Keep selectedRows.memberUpdates on top: after pagination/refetch censusRows
  // comes from the API without locally saved eligibility until Approve runs.
  const censusRowByEmployeeId = new Map(
    censusRows.map((row) => [row.employeeId, row])
  )

  const updatedSelectedRows = selectedRows.map((row) => {
    const latestRow = censusRowByEmployeeId.get(row.employeeId) ?? row
    return {
      ...latestRow,
      memberUpdates: {
        ...latestRow.memberUpdates,
        ...row.memberUpdates,
      },
      rowData: {
        ...latestRow.rowData,
        ClientName: clientName(censusFile?.clientPublicKey),
      },
    }
  })

  const submitCorrections = async (rowsToSubmit: any[]) => {
    // Group corrections by rowId
    const rowsById: Record<number, RowWithCorrections> = rowsToSubmit.reduce(
      (acc, data) => {
        const rowId = data.rowId
        if (!acc[rowId]) {
          acc[rowId] = {
            allRowData: data.allRowData,
            corrections: [],
          }
        }
        // `corrections` array contains all corrections for a given row
        acc[rowId].corrections.push({
          field: data.field,
          newValue: data.newValue,
        })
        return acc
      },
      {}
    )

    const correctedRows: CensusFileRowGridData[] = Object.values(rowsById).map(
      ({ allRowData, corrections }) => {
        corrections.forEach(({ field, newValue }) => {
          if (allRowData?.updates?.updateFields?.[field]) {
            allRowData.updates.updateFields[field].newValue = newValue
          }
          allRowData[field] = newValue
        })
        return allRowData
      }
    )

    const correctedRowIds = new Set(correctedRows.map((row) => row.id))
    const ignoredOnlyRows = Array.from(
      new Map(
        pendingIgnoreCreates
          .filter((p) => !correctedRowIds.has(p.censusRowId))
          .map((p) => [p.censusRowId, p.gridRow])
      ).values()
    )

    const allRowsToSubmit = [...correctedRows, ...ignoredOnlyRows]
    const updatedRows: CensusFileRowDto[] = allRowsToSubmit.map((row) =>
      mapGridDataToCensusFileRowDto(row)
    )

    const ignoreRulesToSend = pendingIgnoreCreates.map(
      ({ employeePublicKey, fieldName, ignoredNewValue, liveValue }) => ({
        employeePublicKey,
        fieldName,
        ignoredNewValue,
        liveValue,
      })
    )

    try {
      await updateCensusFileRows(
        updatedRows,
        publicKey,
        PatchActionEnum.UPDATE_RECORDS,
        ignoreRulesToSend.length
          ? { ignoreRules: ignoreRulesToSend }
          : undefined
      )

      setMode('submit')
      setLocallyIgnoredFields({})
      setPendingIgnoreCreates([])
      setUndoStack([])
    } catch (error: any) {
      if (error?.response?.status === 422) {
        message.error(
          'One or more ignore rules have empty values — empty values are not allowed. Please review and try again.'
        )
      } else {
        message.error('Failed to save edits. Please try again.')
      }
      throw error
    }
  }

  const onCellValueChanged = useCallback(
    (params: CellValueChangedEvent<any>) => {
      const currentCensusRows = [...censusRows] // Take a snapshot before updating

      prevRowsRef.current = currentCensusRows

      const appended = markRowDirty(params)
      if (appended) {
        setUndoStack((prev) => [...prev, { type: 'cell' }])
      } else {
        setUndoStack((prev) => {
          const idx = [...prev].reverse().findIndex((e) => e.type === 'cell')
          if (idx === -1) return prev
          const realIdx = prev.length - 1 - idx
          return [...prev.slice(0, realIdx), ...prev.slice(realIdx + 1)]
        })
      }

      const updatedRows = [...currentCensusRows]
      const updatedRow = updatedRows.find((row) => row.id === params.data.rowId) // Find the edited row
      if (updatedRow) {
        // Access the updates blob inside the row
        const updates = updatedRow?.updates?.updateFields

        // Get the field from params.data (e.g., 'activeBenefits')
        const fieldToUpdate = params.data.field

        if (updates && updates[fieldToUpdate]) {
          // Update the newValue in the updates blob
          updates[fieldToUpdate].newValue = params.newValue // Set the new value for that field

          // Update the censusRows state to trigger a re-render
          setCensusRows(updatedRows) // This will update the grid

          // Optional: Rebuild the row data for AG-Grid if necessary (but this is mostly handled by rowData prop)
        }
      }

      // Refresh the cell to reflect changes (optional)
      params.api.refreshCells({ rowNodes: [params.node] })
    },
    [censusRows, markRowDirty]
  )

  const shouldShowReviewEligibility = (row: CensusFileRowGridData) => {
    if (row.type !== CensusFileRowTypeEnum.UPDATE_EMPLOYEE_NO_BENEFITS) {
      return false
    }

    const hasHireDateChange = !!row.updates?.updateFields?.HireDate
    const hasEmploymentStatusChange =
      !!row.updates?.updateFields?.EmploymentStatus

    return hasHireDateChange || hasEmploymentStatusChange
  }

  const defaultActionButton = (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexWrap: 'nowrap',
        whiteSpace: 'nowrap',
      }}
    >
      <ToggleEditModeButton
        dirtyRows={dirtyRows}
        selectedRows={selectedRows}
        mode={mode} // Pass mode based on the state
        setMode={setMode} // Pass the toggle function to update mode
        pendingEditsCount={undoStack.length}
      />
      {mode === 'edit' ? (
        <InlineEditActionButtons
          undoLastEdit={handleUndoLast}
          editCount={undoStack.length}
          dirtyRows={dirtyRows}
          handleSubmit={handleSubmit}
          cleanAllRows={cleanAllRowsAndStack}
          submitCorrections={submitCorrections}
          updateMode={true}
          setLoading={setLoading}
          pendingIgnoreCreates={pendingIgnoreCreates}
        />
      ) : (
        <>
          <ApproveSelectedUpdatesButton
            publicKey={publicKey}
            selectedRows={updatedSelectedRows}
            handleSubmit={handleSubmit}
            setLoading={setLoading}
            hasPendingChanges={hasPendingChanges}
            pendingIgnoreCreates={[]}
            pendingUnignoreRuleIds={[]}
          />
          <TrackSelectedUpdatesButton
            publicKey={publicKey}
            selectedRows={updatedSelectedRows}
            handleSubmit={handleSubmit}
            setLoading={setLoading}
          />
          <RejectSelectedButton
            publicKey={publicKey}
            selectedRows={selectedRows}
            handleSubmit={handleSubmit}
            setLoading={setLoading}
            selectAllPayload={selectAllPayload}
            totalCount={totalRows}
          />
          {!Array.isArray(censusRowType) ? (
            <CensusReviewCsvExportButton
              publicKey={publicKey}
              exportType={censusRowType as CensusReviewCsvDownloadType}
            />
          ) : null}
        </>
      )}
    </div>
  )

  // Render the Ag-Grid
  return (
    <PageWrapper>
      <Page
        title="Review Records"
        action={
          actionButton
            ? actionButton({
                censusRows,
                selectedRows,
                handleSubmit: resetGrid,
              })
            : defaultActionButton
        }
        breadcrumb={
          <BreadcrumbNav
            items={[
              { title: 'Census Review', path: CENSUS_REVIEW_PATH },
              { title: 'Review Records' },
            ]}
          />
        }
      >
        <AdLayout>
          <Content className={style.content}>
            <div
              style={{
                position: 'relative',
                padding: '15px 15px 0 15px',
              }}
            >
              <FileInfo
                data={censusFile}
                clientName={clientName(censusFile?.clientPublicKey)}
              />
              {/* Pagination Controls */}
              <div
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <Button onClick={goToPreviousPage} disabled={currentPage === 0}>
                  Previous
                </Button>
                <span style={{ margin: '0 10px' }}>
                  Page {currentPage + 1} of {totalPages}
                </span>
                <Button
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
            <BackgroundTicketJobs publicKey={publicKey} />

            {/* Submit and Reject buttons with count of checked items */}
            <ReviewTabs
              publicKey={publicKey}
              counts={counts}
              onTabChange={onTabChange}
              hasPendingChanges={hasBlockingPendingWork}
              currentTab={
                Array.isArray(censusRowType) ? censusRowType[0] : censusRowType
              }
              style={{ padding: '15px 0 0 15px' }}
              showTabs={true}
            />

            {/* Display Employee Name and ID */}
            <div style={{ margin: '0px 16px' }}>
              <div
                style={{
                  margin: '10px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <input
                  type="checkbox"
                  onChange={handleSelectAllInCurrentPageChange}
                  checked={isCurrentPageFullySelected}
                />
                <label style={{ fontWeight: 'bold' }}>
                  Select All on This Page
                </label>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={isAllFullySelected}
                />
                <label style={{ fontWeight: 'bold' }}>
                  Select All ({totalRows})
                </label>
              </div>
              {/* Search field */}
              {showSearch && (
                <div style={{ padding: '8px 0' }}>
                  <Input.Search
                    key={
                      Array.isArray(censusRowType)
                        ? censusRowType.join(',')
                        : censusRowType
                    }
                    placeholder="Search by first or last name"
                    allowClear
                    onSearch={(value) => {
                      setSearchTerm(value.trim())
                      setCheckedItems({})
                      setSelectedRows([])
                      setIsSelectAll(false)
                    }}
                    style={{ width: 280 }}
                  />
                </div>
              )}
              {searchTerm && censusRows.length === 0 && (
                <div
                  style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#999',
                    fontSize: '14px',
                    background: 'white',
                    borderRadius: '8px',
                  }}
                >
                  No records found
                </div>
              )}
            </div>

            {currentEmployees.map((row, index) => (
              <UpdateRecordRow
                key={`${Array.isArray(censusRowType) ? censusRowType.join(',') : censusRowType}-${row.id}`}
                row={row}
                index={index}
                isChecked={checkedItems[row.employeeId] || false}
                isExpanded={!collapsedRows.has(row.employeeId)}
                onToggleExpand={toggleRow}
                onCheckboxChange={handleCheckboxChange}
                nameLink={generateCoverageSummaryLink(
                  row?.updates?.employeePublicKey ?? '',
                  row.firstName,
                  row.lastName
                )}
                activeBenefits={getActiveBenefits(row.updates)}
                colDefs={finalColDefs}
                rowData={populateUpdateRowData(row)}
                getContextMenuItems={getUpdateGridContextMenuItems}
                onCellValueChanged={onCellValueChanged}
                showReviewEligibility={shouldShowReviewEligibility(row)}
                eligibilityData={
                  eligibilityData[row.employeeId] || {
                    benefitsEligible: false,
                    benefitsEffectiveDate: null,
                    enrollmentStartDate: null,
                    enrollmentEndDate: null,
                    doNotCreateEnrollment: false,
                  }
                }
                onToggleChange={handleToggleChange}
                onDateChange={handleDateChange}
                onDoNotCreateEnrollmentChange={
                  handleDoNotCreateEnrollmentChange
                }
                updateMemberUpdates={updateMemberUpdates}
              />
            ))}
          </Content>
        </AdLayout>
      </Page>
    </PageWrapper>
  )
}

export default CensusReviewRecordsBaseUpdate
